export const isValidUrlString = (value) => {
  try {
    const { href, protocol } = new URL(value)

    if (!['http:', 'https:'].includes(protocol)) {
      return false
    }

    return !!href
  } catch {
    return false
  }
}

export const isValidCalendlyUrl = (value) => {
  try {
    if (!isValidUrlString(value)) {
      return false
    }

    const { hostname } = new URL(value)
    const hostnameParts = hostname.split('.')
    const domain = hostnameParts.slice(-2).join('.')

    return domain === 'calendly.com'
  } catch {
    return false
  }
}

export const isValidProfileId = (value) => {
  if (typeof value !== 'string') return false

  const MAX_LENGTH = 64
  const uuidLikePattern = /^[a-zA-Z0-9-]+$/

  return value.length > 0 && value.length <= MAX_LENGTH && uuidLikePattern.test(value)
}

/**
 * Decodes HTML entities (both named and numeric) from a string.
 * Handles decimal (&#64;) and hexadecimal (&#x40;) numeric entities.
 * Note: &amp; must be decoded last to prevent double-decoding of entities like &amp;lt;
 */
const decodeHtmlEntities = (value) => {
  // Decode named entities first (but NOT &amp; - that comes last)
  let decoded = value
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'")

  // Decode hexadecimal numeric entities (&#x27;, &#x2F;, &#x40;, etc.)
  decoded = decoded.replace(/&#x([0-9A-Fa-f]+);/g, (match, hex) => String.fromCharCode(parseInt(hex, 16)))

  // Decode decimal numeric entities (&#39;, &#64;, &#60;, etc.)
  decoded = decoded.replace(/&#(\d+);/g, (match, dec) => String.fromCharCode(parseInt(dec, 10)))

  // Decode &amp; last to prevent double-decoding (e.g., &amp;lt; should become &lt;, not <)
  decoded = decoded.replace(/&amp;/g, '&')

  return decoded
}

/**
 * Iteratively decodes HTML entities until no more changes occur.
 * This handles double-encoded entities (e.g., &amp;lt; -> &lt; -> <)
 */
const iterativelyDecodeEntities = (value) => {
  let decoded = value
  let previousDecoded = ''
  // Keep decoding until no more changes occur (handles multiple levels of encoding)
  while (decoded !== previousDecoded) {
    previousDecoded = decoded
    decoded = decodeHtmlEntities(decoded)
  }
  return decoded
}

/**
 * Strips HTML tags and decodes HTML entities from a string to prevent XSS attacks.
 * This is a defensive sanitization function for contact name/email fields.
 */
export const stripHtmlTags = (value) => {
  if (typeof value !== 'string') return ''

  if (typeof document !== 'undefined') {
    // Use DOMParser to safely decode HTML entities without executing scripts
    try {
      const parser = new DOMParser()
      const doc = parser.parseFromString(value, 'text/html')
      let sanitized = doc.body.textContent || doc.body.innerText || ''
      // DOMParser may not fully decode double-encoded entities, so apply iterative decoding
      sanitized = iterativelyDecodeEntities(sanitized)
      // Remove any remaining HTML tags using regex
      sanitized = sanitized.replace(/<[^>]*>/g, '')
      // Decode one more time in case there were entity-encoded tags that are now exposed
      sanitized = iterativelyDecodeEntities(sanitized)
      return sanitized
    } catch (e) {
      // Fallback if DOMParser fails: decode entities manually then strip tags
      let decoded = iterativelyDecodeEntities(value)
      decoded = decoded.replace(/<[^>]*>/g, '')
      decoded = iterativelyDecodeEntities(decoded)
      return decoded
    }
  }

  // Fallback for non-browser environments: decode entities and strip tags
  // Decode multiple times to handle double-encoded entities (e.g., &amp;lt; -> &lt; -> <)
  const decoded = iterativelyDecodeEntities(value)
  // Then strip HTML tags (both literal tags and any remaining entity-encoded tags)
  let sanitized = decoded.replace(/<[^>]*>/g, '')
  // Decode one more time in case there were entity-encoded tags that are now exposed
  sanitized = iterativelyDecodeEntities(sanitized)
  return sanitized
}

/**
 * Maximum allowed length for pane prop values.
 * Prevents oversized or malicious payloads from being passed through query strings.
 */
const MAX_PROP_VALUE_LENGTH = 500
/**
 * Recursively sanitizes pane state props to enforce size limits, URL validity, and known key constraints.
 *
 * - Strings longer than `MAX_PROP_VALUE_LENGTH` are replaced with an empty string.
 * - Strings that look like URLs are validated using `isValidCalendlyUrl`; invalid URLs are replaced with an empty string.
 * - The `profile` key is only preserved if `isValidProfileId` returns true.
 * - Contact `name` and `email` fields are sanitized to strip HTML tags and decode entities to prevent XSS attacks.
 * - Nested objects and arrays are sanitized recursively.
 * - Primitive values (numbers, booleans, null, etc.) are passed through unchanged.
 *
 * This function is intended to prevent invalid, unsafe, or overly large data
 * from being persisted to `paneState` (e.g., in URL parameters) or shared across MFEs.
 *
 * @param {Record<string, unknown>} props - The parsed pane props from a decoded base64 query string.
 * @returns {Record<string, string>} A sanitized version of the props object, safe to pass to UI components.
 */
export const sanitizePaneProps = (props) => {
  const result = {}

  for (const [key, value] of Object.entries(props)) {
    if (key === 'profile') {
      if (isValidProfileId(value)) {
        result[key] = value
      }
    } else if (key === 'name' || key === 'email') {
      // Sanitize contact name and email fields to prevent XSS attacks
      // Strip HTML tags and decode HTML entities
      if (typeof value === 'string') {
        if (value.length > MAX_PROP_VALUE_LENGTH) {
          result[key] = ''
        } else {
          result[key] = stripHtmlTags(value)
        }
      } else {
        result[key] = value
      }
    } else if (typeof value === 'string') {
      if (value.length > MAX_PROP_VALUE_LENGTH) {
        result[key] = ''
      } else if (key === 'linkedIn') {
        result[key] = isValidUrlString(value) ? value : ''
      } else if (isValidUrlString(value)) {
        result[key] = isValidCalendlyUrl(value) ? value : ''
      } else {
        result[key] = value
      }
    } else if (Array.isArray(value)) {
      result[key] = value.map((item) => (typeof item === 'object' && item !== null
        ? sanitizePaneProps(item)
        : item)
      )
    } else if (key === 'avatar' && value instanceof File) {
      // Allow it, pass it through unchanged
      result[key] = value
    } else if (value && typeof value === 'object') {
      result[key] = sanitizePaneProps(value)
    } else {
      result[key] = value
    }
  }

  return result
}
